import {
    Box,
    IconButton,
    SxProps,
    Theme,
    Checkbox,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Typography,
    Grid2,
    Alert,
    Chip,
} from '@mui/material';
import BaseCard from '@/components/ui/BaseCard';
import { Visibility, DragHandle, LinkOff, PushPin } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import LayoutSubheading from './LayoutSubheading';
import BaseAutocomplete from '../form/BaseAutocomplete';
import { DisplayOption, LAYOUT_MODE, LayoutList, MULTI_LAYOUT_SLUG, MultiLayoutOptions } from '@/types/pages/layout';
import BaseButton from '../ui/BaseButton';
import { useEffect, useState } from 'react';

import imgColumn2 from '@/assets/images/storeManagement/multiLayout/column-2.png';
import imgColumn3 from '@/assets/images/storeManagement/multiLayout/column-3.png';
import imgLandscapeGrid4 from '@/assets/images/storeManagement/multiLayout/landscape-grid-4.png';
import imgLandscapeList2 from '@/assets/images/storeManagement/multiLayout/landscape-list-2.png';
import imgLandscapeList5 from '@/assets/images/storeManagement/multiLayout/landscape-list-5.png';
import imgLandscapeRange6 from '@/assets/images/storeManagement/multiLayout/landscape-range-6.png';
import imgList2 from '@/assets/images/storeManagement/multiLayout/list-2.png';
import imgPortraitGrid4 from '@/assets/images/storeManagement/multiLayout/portrait-grid-4.png';
import imgPortraitList3 from '@/assets/images/storeManagement/multiLayout/portrait-list-3.png';
import imgPortraitList6 from '@/assets/images/storeManagement/multiLayout/portrait-list-6.png';
import imgPortraitRange8 from '@/assets/images/storeManagement/multiLayout/portrait-range-8.png';

import { useTranslation } from 'react-i18next';

type DisplayTypeCardProps = {
    sx?: SxProps<Theme>;
    viewMode: boolean | undefined;
    defaultLayoutId: string | null;
    mode: LAYOUT_MODE;
    options: DisplayOption[];
    layouts: LayoutList[];
    value: string;
    onChange: (newValue: string) => void;
    onView: (id: string) => void;
    selectStyleId?: number | null;
    setSelectStyleId?: (id: number) => void;
    onDetach?: (newList: string[]) => void;
    onReorder?: (newList: LayoutList[]) => void;
    onSetDefault?: (uuid: string) => void;
};

interface WrappedItem {
    uid: string;
    data: LayoutList;
}
const MULTI_LAYOUT_IMG: Record<MULTI_LAYOUT_SLUG, string> = {
    [MULTI_LAYOUT_SLUG.COLUMN_2]: imgColumn2,
    [MULTI_LAYOUT_SLUG.COLUMN_3]: imgColumn3,
    [MULTI_LAYOUT_SLUG.LANDSCAPE_GRID_4]: imgLandscapeGrid4,
    [MULTI_LAYOUT_SLUG.LANDSCAPE_LIST_2]: imgLandscapeList2,
    [MULTI_LAYOUT_SLUG.LANDSCAPE_LIST_5]: imgLandscapeList5,
    [MULTI_LAYOUT_SLUG.LANDSCAPE_RANGE_6]: imgLandscapeRange6,
    [MULTI_LAYOUT_SLUG.LIST_2]: imgList2,
    [MULTI_LAYOUT_SLUG.PORTRAIT_GRID_4]: imgPortraitGrid4,
    [MULTI_LAYOUT_SLUG.PORTRAIT_LIST_3]: imgPortraitList3,
    [MULTI_LAYOUT_SLUG.PORTRAIT_LIST_6]: imgPortraitList6,
    [MULTI_LAYOUT_SLUG.PORTRAIT_RANGE_8]: imgPortraitRange8,
};
const MultiOptions = ({
    options,
    selectedId,
    onSelect,
}: {
    options: MultiLayoutOptions[];
    selectedId: number | null | undefined;
    onSelect: ((id: number) => void) | undefined;
}) => {
    const { t } = useTranslation('common');
    if (options.length === 0) {
        return (
            <Alert
                severity="error"
                sx={{
                    width: '100%',
                    gridColumn: '1 / -1',
                    mt: 2,
                }}
            >
                {t("The Display Type doesn't support Multi Layouts.")}
            </Alert>
        );
    }

    return options.map((option) => {
        const isSelected = option.id === selectedId;
        return (
            <Grid2
                sx={{
                    flex: '0 1 1',
                }}
                key={option.id}
            >
                <Card
                    elevation={isSelected ? 6 : 2}
                    sx={{
                        height: 160,
                        maxWidth: '140px',
                        borderRadius: 2,
                        border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            boxShadow: 6,
                            borderColor: '#1976d2',
                        },
                    }}
                >
                    <CardActionArea
                        onClick={() => onSelect?.(option.id)}
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            p: 1,
                        }}
                    >
                        <CardMedia
                            component="img"
                            image={MULTI_LAYOUT_IMG[option.slug]}
                            alt={option.name}
                            sx={{
                                height: 80,
                                width: '100%',
                                objectFit: 'contain',
                                mb: 2,
                            }}
                        />
                        <CardContent sx={{ p: 0 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    color: isSelected ? '#1976d2' : 'text.primary',
                                }}
                            >
                                {option.name}
                            </Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>
            </Grid2>
        );
    });
};

export default function DisplayTypeCard({
    viewMode,
    mode,
    options,
    value,
    onChange,
    layouts,
    onView,
    sx = {},
    defaultLayoutId,
    onDetach,
    onReorder,
    onSetDefault,
    selectStyleId,
    setSelectStyleId,
}: DisplayTypeCardProps) {
    const { t } = useTranslation('common');
    const selectedOption = options.find((opt) => opt.value === value) ?? null;
    const title = mode === LAYOUT_MODE.MULTI ? t('Associated Sub Layouts') : t('Associated Layouts');

    const [items, setItems] = useState<WrappedItem[]>([]);

    useEffect(() => {
        setItems(
            layouts.map((layout) => ({
                uid: `${layout.id}-${Math.random().toString(36).substring(2, 8)}`,
                data: layout,
            })),
        );
    }, [layouts]);

    const [checkedUIDs, setCheckedUIDs] = useState<string[]>([]);
    const toggleCheck = (uid: string, on: boolean) => {
        setCheckedUIDs((curr) => (on ? [...curr, uid] : curr.filter((x) => x !== uid)));
    };

    const checkedLayoutsId = items.filter((item) => checkedUIDs.includes(item.uid)).map((o) => o.data.uuid);

    const handleDetachSelected = () => {
        if (!viewMode && checkedUIDs.length) {
            onDetach?.(checkedLayoutsId);
            setCheckedUIDs([]);
        }
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const next = Array.from(items);
        const [moved] = next.splice(result.source.index, 1);
        next.splice(result.destination.index, 0, moved);
        setItems(next);
        if (!viewMode) {
            onReorder?.(next.map((w) => w.data));
        }
    };

    const handleSetDefault = () => {
        if (!viewMode && checkedUIDs.length) {
            onSetDefault?.(checkedLayoutsId[0]);
            setCheckedUIDs([]);
        }
    };
    const showCheckbox = Boolean(!viewMode);

    return (
        <BaseCard
            sx={{
                width: '100%',
                boxSizing: 'border-box',
                mb: 0.5,
                p: { xs: 1, sm: '0.5rem 1.5rem' },
                ...sx,
            }}
        >
            <LayoutSubheading>{t('Display Type')}</LayoutSubheading>
            <BaseAutocomplete<DisplayOption, false, false, false>
                size="small"
                value={selectedOption}
                options={options}
                sx={{ mb: 2 }}
                getOptionLabel={(opt) => opt.label}
                onChange={(_, newVal) => newVal && onChange(newVal.value)}
            />
            {mode === LAYOUT_MODE.MULTI && (
                <>
                    <LayoutSubheading>{t('Style Selection')}</LayoutSubheading>
                    <Grid2 container spacing={3} sx={{ mb: 2 }}>
                        <MultiOptions
                            options={selectedOption?.multiLayoutOptions ?? []}
                            selectedId={selectStyleId}
                            onSelect={setSelectStyleId}
                        />
                    </Grid2>
                </>
            )}

            <LayoutSubheading>{title}</LayoutSubheading>
            <BaseCard
                sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    p: { xs: 1, sm: '0.5rem 1.5rem' },
                    my: 1.5,
                    height: { xs: 250, sm: 300, md: 415 },
                    overflowY: 'auto',
                }}
            >
                {/* header row */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: showCheckbox ? '5% 10% 1fr 10% 10%' : '10% 1fr 10% 10%',
                        columnGap: 1,
                        px: 1,
                        py: 1,
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                    }}
                >
                    {showCheckbox && <Box />}
                    {t('Index')}{' '}
                    <Box>{mode === LAYOUT_MODE.MULTI ? `${t('Sub')} ${t('Layout Name')}` : t('Layout Name')}</Box>
                </Box>

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="assoc-layouts" isDropDisabled={viewMode}>
                        {(provided) => (
                            <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ overflowY: 'auto' }}>
                                {items.map(({ uid, data }, idx) => (
                                    <Draggable key={uid} draggableId={uid} index={idx} isDragDisabled={viewMode}>
                                        {(prov) => (
                                            <Box
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                onClick={
                                                    showCheckbox
                                                        ? () => toggleCheck(uid, !checkedUIDs.includes(uid))
                                                        : undefined
                                                }
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: showCheckbox
                                                        ? '5% 10% 1fr 40px 30px'
                                                        : '10% 1fr 40px 30px',
                                                    columnGap: 1,
                                                    alignItems: 'center',
                                                    boxShadow: 1,
                                                    borderRadius: 1,
                                                    px: 1,
                                                    py: 1,
                                                    mb: 1,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {showCheckbox && (
                                                    <Checkbox size="small" checked={checkedUIDs.includes(uid)} />
                                                )}

                                                <Box fontWeight={500}>#{idx}</Box>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    <span>{data.name}</span>
                                                    {data.uuid === defaultLayoutId && (
                                                        <Chip
                                                            label={t('Default')}
                                                            size="small"
                                                            color="error"
                                                            sx={{
                                                                height: 25,
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    )}
                                                </Box>

                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onView(data.id);
                                                    }}
                                                >
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                                {!viewMode && (
                                                    <Box
                                                        {...prov.dragHandleProps}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            cursor: 'grab',
                                                        }}
                                                    >
                                                        <DragHandle fontSize="small" />
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Draggable>
                                ))}

                                {provided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                </DragDropContext>
            </BaseCard>
            {!viewMode && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1, gap: 2 }}>
                    <BaseButton
                        startIcon={<PushPin />}
                        color="info"
                        disabled={checkedUIDs.length !== 1 || checkedLayoutsId.includes(defaultLayoutId as string)}
                        onClick={handleSetDefault}
                    >
                        {t('Set as Default')}
                    </BaseButton>
                    <BaseButton
                        startIcon={<LinkOff />}
                        color="error"
                        disabled={checkedUIDs.length === 0}
                        onClick={handleDetachSelected}
                    >
                        {t('Detach')}
                    </BaseButton>
                </Box>
            )}
        </BaseCard>
    );
}
